import React from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { CreateOutlined } from '@mui/icons-material';
import FullCalendar, { EventInput } from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { useStyles } from './AvailabilityCalendar.styles';
import { CalendarDateService } from '@uplift/services';
import { WeekDay, WeekDayKeys } from '@uplift/types';
import { useTimezone } from '@uplift/contexts';
import { EventItem } from '../EventItem';
import { useL10n } from '@uplift/hooks';
import { UpliftTZPlugin } from '../ScheduleCalendar/uplift-tz-plugin';
import { AvailabilityTimesList } from './AvailabilityTimesList';
import { Loader } from '../../Loader';
import { AvailabilityTips } from './AvailabilityTips';
import { UpliftCalendarStylesWrapper } from '../UpliftCalendarStylesWrapper';
import { AvailabilityTemplate } from './AvailabilityCalendar.types';
import { EditingAvailabilityDayPopup, EditingAvailabilityDay, LiveAvailabilityData } from './EditingAvailabilityDayPopup';


export type AvailabilityCalendarProps = {
  availabilityTemplate: AvailabilityTemplate;
  onAvailabilityTemplateChange: (newTemplate: AvailabilityTemplate) => Promise<void>;
  loading?: boolean;
  warningOnLessThenHour?: boolean;
};


export const AvailabilityCalendar: React.FC<AvailabilityCalendarProps> = ({ availabilityTemplate: _availabilityTemplate, warningOnLessThenHour, ...props }) => {
  const timezone = useTimezone();
  const l10n = useL10n();
  const { classes } = useStyles();
  const [ calendarRoot, setCalendarRoot ] = React.useState<HTMLDivElement | null>();
  const [ editingAvailabilityTemplate, setEditingAvailabilityTemplate ] = React.useState<EditingAvailabilityDay | undefined>();
  const [ dynamicAvailability, setDynamicAvailability ] = React.useState<LiveAvailabilityData>();
  const now = React.useMemo(() => {
    // return fixed date for availability calendar
    // because there are shown only single week
    return CalendarDateService.date('2022-09-12T00:00:00').tz(timezone).startOf('week');
  }, [ timezone ]);
  const availabilityTemplate = React.useMemo(() => {
    if (dynamicAvailability) {
      return {
        ..._availabilityTemplate,
        [dynamicAvailability.weekday]: CalendarDateService.squashTimeRanges(dynamicAvailability.ranges),
      };
    }
    return _availabilityTemplate;
  }, [ _availabilityTemplate, dynamicAvailability ]);
  const allRanges = React.useMemo(() => {
    const keys = Object.keys(availabilityTemplate) as WeekDayKeys[];

    const formatted = keys.map(key => {
      return availabilityTemplate?.[key]?.map(range => {
        const d = now.day(WeekDay[key]).tz(timezone);
        const rFrom = CalendarDateService.date(range.start, 'HH:mm:ss');
        const start = d
          .set('hour', rFrom.hour())
          .set('minute', rFrom.minute());

        const rTo = CalendarDateService.date(range.end, 'HH:mm:ss');
        const daysDelta = range.end === '00:00:00' ? 1 : 0;
        const end = d
          .set('hour', rTo.hour())
          .set('minute', rTo.minute())
          .add(daysDelta, 'day');

        return { start, end };
      }) ?? [];
    });

    return formatted.flat();
  }, [ availabilityTemplate, now, timezone ]);

  const events: EventInput[] = React.useMemo(() => {
    return allRanges.map((e, i) => ({
      id: i.toString(),
      start: e.start.format(),
      end: e.end.format(),
    }));
  }, [ allRanges ]);

  const summary = React.useMemo(() => {
    return allRanges.reduce<number>((prev, { start, end }) => {
      return prev + end.diff(start, 'minutes');
    }, 0) / 60;
  }, [ allRanges ]);

  const defaultScrollTime = React.useMemo(() => {
    if (allRanges.length) {
      const orderedBySmallest = [ ...allRanges ];

      orderedBySmallest.sort((a, b) => a.start.diff(b.start.set('date', a.start.date()), 'hours'));
      const smallestTime = orderedBySmallest?.[0].start;
      const minMinutes = smallestTime.diff(smallestTime.startOf('day'), 'minutes');
      const sub = Math.min(minMinutes, 90);
      const result = smallestTime.subtract(sub, 'minutes').format?.('HH:mm:ss');

      return result;
    }

    return '07:30:00';
  }, [ allRanges ]);

  const calendarKey = React.useMemo(() => [
    timezone,
    props.loading ? 'loading' : 'loaded',
  ].join('-'), [ timezone, props.loading ]);

  return (
    <>
      <UpliftCalendarStylesWrapper ref={setCalendarRoot} className={classes.calendarRoot}>
        <FullCalendar
          eventOverlap={false}
          viewClassNames="availability-calendar"
          scrollTime={defaultScrollTime}
          scrollTimeReset={false}
          key={calendarKey}
          timeZone={timezone}
          now={() => now.toDate()}
          height="100%"
          initialView="timeGridWeek"
          plugins={[ interactionPlugin, timeGridPlugin, UpliftTZPlugin ]}
          headerToolbar={false}
          events={events}
          allDaySlot={false}
          selectable
          eventClick={arg => {
            const date = CalendarDateService.date(arg.event.start).tz(timezone);
            const day = WeekDay[date.day()] as WeekDayKeys;
            const dayTimes = availabilityTemplate[day];

            setEditingAvailabilityTemplate({
              weekday: day,
              availability: dayTimes ?? [],
            });
          }}
          select={arg => {
            const date = CalendarDateService.date(arg.start).tz(timezone);
            const day = WeekDay[date.day()] as WeekDayKeys;
            const dayTimes = availabilityTemplate[day];

            setEditingAvailabilityTemplate({
              weekday: day,
              availability: dayTimes ?? [],
            });
          }}
          eventContent={arg => {
            if (!arg.event.start || !arg.event.end) return null;
            const start = CalendarDateService.date(arg.event.start).tz(timezone);
            const end = CalendarDateService.date(arg.event.end).tz(timezone);
            const isWarning = warningOnLessThenHour && end.diff(start, 'minutes') < 60;

            const elevation = editingAvailabilityTemplate?.weekday !== undefined && start.day() === WeekDay[editingAvailabilityTemplate.weekday];

            return (
              <EventItem
                showFullRange
                elevation={elevation}
                startTime={arg.event.start}
                endTime={arg.event.end}
                isDayView={false}
                timezone={timezone}
                variant={isWarning ? 'availabilityWarning' : 'availability'}
              />
            );
          }}
          dayHeaderContent={arg => {
            const date = CalendarDateService.date(arg.date).utc();
            const weekdayName = date.format('dddd');
            const day = WeekDay[date.day()] as WeekDayKeys;
            const dayTimes = availabilityTemplate[day];
            const isAvailabilityAvailable = !!dayTimes?.length;
            const handleEditTemplate = () => {
              setEditingAvailabilityTemplate({
                weekday: day,
                availability: dayTimes ?? [],
              });
            };

            return (
              <Box display="flex" flexDirection="column" maxWidth="128px" width="100%" zIndex={1}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography color="textPrimary" variant="body2" style={{ fontWeight: 'bold' }}>{weekdayName}</Typography>
                  <IconButton disabled={props.loading} onClick={handleEditTemplate} color="secondary" size="small"><CreateOutlined fontSize="small" /></IconButton>
                </Box>
                {isAvailabilityAvailable ? (
                  <AvailabilityTimesList timeRanges={dayTimes} />
                ) : (
                  <Box pb={2}>
                    <Typography variant="body2" className={classes.timeListEmpty}>{l10n.calendar.availability.availabilityCalendar.unavailableDate}</Typography>
                  </Box>
                )}
              </Box>
            );
          }}
        />
        {props.loading && (
          <Box className={classes.loading}>
            <Loader />
          </Box>
        )}
        <AvailabilityTips />
      </UpliftCalendarStylesWrapper>
      <Box className={classes.metrics}>
        <Typography><strong>{summary}</strong> {l10n.calendar.availability.availabilityCalendar.availableHours}</Typography>
      </Box>
      {calendarRoot && editingAvailabilityTemplate && (
        <EditingAvailabilityDayPopup
          calendarRoot={calendarRoot}
          onLiveChange={setDynamicAvailability}
          warningOnLessThenHour={warningOnLessThenHour}
          editingAvailabilityTemplate={editingAvailabilityTemplate}
          setEditingAvailabilityTemplate={setEditingAvailabilityTemplate}
          onAvailabilityChange={async (ranges) => {
            if (!editingAvailabilityTemplate?.weekday) return;

            await props.onAvailabilityTemplateChange?.({
              ...availabilityTemplate,
              [editingAvailabilityTemplate.weekday]: ranges,
            });
          }}
        />
      )}
    </>
  );
};
